import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-translate-plus';
import styled, { createGlobalStyle } from 'styled-components';

import {
  Button,
  ContextMenu,
  ContextMenuLink,
  ContextMenuSeparator,
  Footer,
  FooterLink,
  Icon,
  icons,
  PositioningPortalWithState,
} from '@hoffmann/pattern-library-2';

import { ReactComponent as LanguageSvg } from '../../assets/images/translate_black.svg';
import { useTagCommander } from '../../hooks/tracking/useTagCommander/useTagCommander';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useUserProfileLocaleToast } from '../../hooks/useUserProfileLocaleToast';
import {
  getCountryImage,
  getDefaultAndSelectedSupportedLocales,
  getLocaleForLanguage,
  getSupportedLocalesWithoutDefaultAndSelected,
  AllSupportedCountries,
  SupportedLocale,
  supportedLocales,
  allSupportedLocaleSetting,
} from '../../services/locale';
import { useLocalisation } from '../../services/localisationContext';
import { AboutUsModal } from './AboutUsModal';
import { TermsOfUseModal } from './TermsOfUseModal';

export const FOOTER_HEIGHT = 53;

const ConfiguratorFooter: React.FC = () => {
  const t = useTranslate('Footer');
  const history = useNavigate();
  useUserProfileLocaleToast();

  const { currentLanguage, currentCountry } = useLocalisation();
  const [, setLocale] = useLocalStorage('locale');
  const [selectedCountry, setSelectedCountry] = useLocalStorage<string>('selectedCountry');
  const { showCookieBanner } = useTagCommander();

  const [isAboutUsModalOpen, setIsAboutUsModalOpen] = useState(false);
  const [isConditionsOfUseModalOpen, setIsConditionsOfUseModalOpen] = useState(false);

  const handleChangeLanguage = async (locale: SupportedLocale) => {
    setLocale(locale);
    // trigger page reload
    history(0);
  };

  const handleChangeCountry = async (country: string) => {
    setSelectedCountry(country);
  };

  return (
    <Styles>
      <GlobalStyle />
      <Footer
        classNames={['configurator-footer']}
        left={t('LABEL_COPYRIGHT')}
        right={
          <>
            <FooterLink label={t('BUTTON_IMPRINT')} onClick={() => history('/imprint')} />
            <FooterLink
              href={t('URL_PRIVACY')}
              label={t('BUTTON_PRIVACY')}
              onClick={() => window.open(t('URL_PRIVACY'), '_blank')}
            />
            <FooterLink label={t('BUTTON_ABOUT')} onClick={() => setIsAboutUsModalOpen(true)} />
            <FooterLink
              label={t('BUTTON_TERMS')}
              onClick={() => setIsConditionsOfUseModalOpen(true)}
            />
            <FooterLink
              label={t('BUTTON_COOKIES')}
              onClick={() => showCookieBanner(t('URL_PRIVACY'))}
              href={t('URL_PRIVACY')}
            />
            {currentCountry && (
              <PositioningPortalWithState
                portalContent={({ close }) => (
                  <ContextMenu onClose={close} classNames={['languages-menu']}>
                    {Object.keys(AllSupportedCountries).map((country: string) => (
                      <ContextMenuLinkWrapper key={country}>
                        <ContextMenuLink
                          icon={getCountryImage(country)}
                          label={allSupportedLocaleSetting.get(country).name}
                          classNames={['language-link']}
                          onClick={() => handleChangeCountry(country)}
                        />
                        {country.toUpperCase() === selectedCountry?.toUpperCase() && (
                          <SelectedIcon>
                            <Icon source={icons.Check} />
                          </SelectedIcon>
                        )}
                      </ContextMenuLinkWrapper>
                    ))}
                  </ContextMenu>
                )}
                positionStrategy={(parentRect, portalRect) => ({
                  left: parentRect.left - portalRect.width + parentRect.width,
                  top: parentRect.top - portalRect.height,
                })}
              >
                {({ open }) =>
                  selectedCountry && (
                    <Button
                      icon={getCountryImage(selectedCountry, '15')}
                      label={
                        allSupportedLocaleSetting.get(AllSupportedCountries[selectedCountry]).name
                      }
                      ghost
                      navigation
                      classNames={['language-button-icon']}
                      onClick={open}
                    />
                  )
                }
              </PositioningPortalWithState>
            )}
            <PositioningPortalWithState
              portalContent={({ close }) => (
                <ContextMenu onClose={close} classNames={['languages-menu']}>
                  {[...getDefaultAndSelectedSupportedLocales(currentLanguage, selectedCountry)].map(
                    ([locale, languageName]) => (
                      <ContextMenuLinkWrapper key={locale}>
                        <ContextMenuLink
                          classNames={['language-link']}
                          key={locale}
                          label={languageName}
                          onClick={() => handleChangeLanguage(locale)}
                        />
                        {locale.split('-')[0].toUpperCase() === currentLanguage.toUpperCase() && (
                          <SelectedIcon>
                            <Icon source={icons.Check} />
                          </SelectedIcon>
                        )}
                      </ContextMenuLinkWrapper>
                    ),
                  )}
                  <ContextMenuSeparator />
                  {[
                    ...getSupportedLocalesWithoutDefaultAndSelected(
                      currentLanguage,
                      selectedCountry,
                    ),
                  ].map(([locale, languageName]) => (
                    <ContextMenuLinkWrapper key={locale}>
                      <ContextMenuLink
                        classNames={['language-link']}
                        key={locale}
                        label={languageName}
                        onClick={() => handleChangeLanguage(locale)}
                      />
                    </ContextMenuLinkWrapper>
                  ))}
                </ContextMenu>
              )}
              positionStrategy={(parentRect, portalRect) => ({
                left: parentRect.left - portalRect.width + parentRect.width,
                top: parentRect.top - portalRect.height,
              })}
            >
              {({ open }) => (
                <Button
                  icon={<LanguageSvg />}
                  label={supportedLocales.get(getLocaleForLanguage(currentLanguage))}
                  ghost
                  navigation
                  classNames={['language-button-icon']}
                  onClick={open}
                />
              )}
            </PositioningPortalWithState>
          </>
        }
      />
      {isAboutUsModalOpen && (
        <AboutUsModal
          onOpenConditions={() => setIsConditionsOfUseModalOpen(true)}
          onClose={() => setIsAboutUsModalOpen(false)}
          t={t}
        />
      )}
      {isConditionsOfUseModalOpen && (
        <TermsOfUseModal onClose={() => setIsConditionsOfUseModalOpen(false)} t={t} />
      )}
    </Styles>
  );
};

export default ConfiguratorFooter;

const Styles = styled.div`
  max-height: ${FOOTER_HEIGHT}px;

  .configurator-footer {
    background: white;
  }
`;

const GlobalStyle = createGlobalStyle`
  .hpl2-PositioningPortal .hpl2-ContextMenu.languages-menu {
    max-height: 250px;
    overflow-y: auto;
  }
  .language-button-icon svg{
    margin-right: 8px;
    display: inline-block;
    width: 1em;
    height: 1em;
    fill: currentColor;
    vertical-align: middle;
  }
  .language-link {
    padding-left: 1rem;
  }
`;

const ContextMenuLinkWrapper = styled.div`
  width: 220px;
  position: relative;
  a {
    height: 2rem;
  }
`;

const SelectedIcon = styled.div`
  position: absolute;
  top: 0.5rem;
  height: 2rem;
  right: 1rem;
  color: ${({ theme }) => theme.colors.environmentColor};
`;
